import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { CoreBox, CoreChip, CoreTypography } from '@youscience/core';
import { MajorDetail } from '@common-types';

import { CollegeLocation } from '@interfaces/location';
import { StateSpotlight } from '@interfaces/stateSpotlight';

import { getSchoolsByMajorPath } from '@constants/ROUTES';
import { USHE_GROUPED_SCHOOLS_VISIBLE, getStatePublicCollegesLabel } from '@constants/stateSpotlight';

import { getFromSchoolLocation } from '@utils/breadcrumbs';

import { Container } from '@components/Layout/components/Container';
import { SchoolCard } from '@components/SchoolCard';
import ActionButton from '@components/ActionButton';

import { sxStyles, StyledSchoolsGrid } from './WhereYouCanStudyIt.styles';

type MajorDetailSchool = MajorDetail['schools'][number];

interface WhereYouCanStudyItProps {
  cipCode: string;
  schools: MajorDetailSchool[];
  spotlight: StateSpotlight;
}

const getSchoolGroups = (stateName: string) => [
  { title: getStatePublicCollegesLabel(stateName), isInGroup: (school: MajorDetailSchool) => !!school.isUshe },
  { title: `Other ${stateName} schools`, isInGroup: (school: MajorDetailSchool) => !school.isUshe && !!school.isUtah },
  { title: 'Other states', isInGroup: (school: MajorDetailSchool) => !school.isUshe && !school.isUtah },
];

export const WhereYouCanStudyIt = ({ cipCode, schools, spotlight }: WhereYouCanStudyItProps) => {
  const location: CollegeLocation = useLocation();
  const [isExpanded, setIsExpanded] = useState(false);

  if (!schools.length) return null;

  const shownSchools = isExpanded ? schools : schools.slice(0, USHE_GROUPED_SCHOOLS_VISIBLE);
  const hiddenCount = schools.length - USHE_GROUPED_SCHOOLS_VISIBLE;

  return (
    <CoreBox data-testid="whereYouCanStudyIt" sx={sxStyles.sectionBg}>
      <Container>
        <CoreBox sx={sxStyles.header}>
          <CoreTypography sx={sxStyles.title} variant="h3" component="h2">
            Where you can study it
          </CoreTypography>

          <Link
            data-testid="seeItInSchools"
            to={getSchoolsByMajorPath(cipCode)}
            state={getFromSchoolLocation(location.pathname)}
          >
            <CoreChip label="See it in Schools" variant="outlined" sx={sxStyles.chip} />
          </Link>
        </CoreBox>

        {getSchoolGroups(spotlight.stateName).map(({ title, isInGroup }) => {
          const groupSchools = shownSchools.filter(isInGroup);

          if (!groupSchools.length) return null;

          return (
            <CoreBox key={title} data-testid="schoolGroup" sx={sxStyles.group}>
              <CoreTypography sx={sxStyles.groupTitle} variant="h5" component="h3">
                {title}
              </CoreTypography>

              <StyledSchoolsGrid>
                {groupSchools.map((school) => (
                  <SchoolCard key={school.id} school={school} schoolCardType="reduced" componentId="study-it-school" />
                ))}
              </StyledSchoolsGrid>
            </CoreBox>
          );
        })}

        {hiddenCount > 0 && (
          <ActionButton
            data-testid="toggleStudyItSchools"
            variant="outlined"
            sx={sxStyles.toggle}
            onClick={() => setIsExpanded((value) => !value)}
          >
            {isExpanded ? 'Show fewer schools' : `Show ${hiddenCount} more schools`}
          </ActionButton>
        )}
      </Container>
    </CoreBox>
  );
};
